import { useEffect, useState } from "react";
import { useAuth } from "@clerk/react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { makeApi } from "@/lib/api";
import { AddressInput, ADDRESS_HINT } from "@/components/AddressInput";
import { DriverSelect } from "@/components/loads/DriverSelect";
import { BrokerPicker } from "@/components/brokers/BrokerPicker";
import { FieldLabel, AiBanner, AiConfirm, applyExtraction } from "@/components/ai/AiFill";
import { AppointmentInput } from "@/components/loads/AppointmentInput";
import { CarrierNumberField } from "@/components/loads/CarrierNumberField";

const FIELDS = ["carrier_number", "load_number", "broker_name", "broker_id", "rate", "pickup_location", "delivery_location", "assigned_driver_id", "pickup_date", "delivery_date", "pickup_appt", "delivery_appt"];

// prefill: optional AI extraction result { fields, flagged, notes } to overlay on the load.
export const EditLoadDialog = ({ open, load, drivers, onClose, onSaved, prefill }) => {
  const { getToken } = useAuth();
  const [form, setForm] = useState({});
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [marks, setMarks] = useState(null);
  const [confirmed, setConfirmed] = useState(false);

  useEffect(() => {
    if (open && load) {
      const base = Object.fromEntries(FIELDS.map((k) => [k, k === "rate" ? String(load.rate) : load[k] ?? (k.endsWith("_date") || k.endsWith("_appt") ? "" : null)]));
      const applied = prefill ? applyExtraction(base, prefill) : { form: base, marks: null };
      if (prefill?.broker_match) applied.form = { ...applied.form, broker_id: prefill.broker_match.id, broker_name: prefill.broker_match.name };
      else if (applied.marks?.broker_name === "filled" && applied.form.broker_name !== load.broker_name) applied.form = { ...applied.form, broker_id: null };
      setForm(applied.form);
      setMarks(applied.marks);
      setConfirmed(false);
      setError("");
    }
  }, [open, load?.id, prefill]); // eslint-disable-line react-hooks/exhaustive-deps

  const set = (k) => (v) => {
    setForm((f) => ({ ...f, [k]: v?.target ? v.target.value : v }));
    setMarks((m) => (m && m[k] ? { ...m, [k]: undefined } : m));
  };
  const rate = parseFloat(form.rate);
  const valid = ["load_number", "broker_name", "pickup_location", "delivery_location"].every((k) => (form[k] || "").trim()) && rate > 0;
  const invoiceSent = load?.invoice?.status === "Sent";

  const save = async (e) => {
    e.preventDefault();
    if (marks && !confirmed) return;
    setBusy(true);
    setError("");
    try {
      await makeApi(getToken).patch(`/loads/${load.id}`, {
        ...form, carrier_number: (form.carrier_number || "").trim() || null, rate, pickup_date: form.pickup_date || null, delivery_date: form.delivery_date || null,
        pickup_appt: form.pickup_appt || null, delivery_appt: form.delivery_appt || null,
      });
      toast.success("Load updated");
      onSaved();
      onClose();
    } catch (err) {
      setError(err?.response?.data?.detail?.toString() || "Could not save changes");
    } finally {
      setBusy(false);
    }
  };

  const field = (k, label, extra = {}) => (
    <div>
      <FieldLabel htmlFor={`edit-${k}`} mark={marks?.[k]} testId={`edit-${k}`}>{label}</FieldLabel>
      <Input id={`edit-${k}`} className="mt-1.5" value={form[k] ?? ""} onChange={set(k)} data-testid={`edit-${k}-input`} {...extra} />
    </div>
  );

  return (
    <Dialog open={open} onOpenChange={(v) => !v && !busy && onClose()}>
      <DialogContent
        className="max-w-2xl"
        data-testid="edit-load-dialog"
        onEscapeKeyDown={(e) => { if (document.querySelector("[data-address-suggestions]")) e.preventDefault(); }}
      >
        <DialogHeader>
          <DialogTitle>{marks ? "Review auto-filled details" : `Edit load #${load?.carrier_number || load?.load_number}`}</DialogTitle>
          <DialogDescription>Changes save to this same load and its documents stay attached.</DialogDescription>
        </DialogHeader>
        <form onSubmit={save} className="space-y-4 max-h-[70vh] overflow-y-auto pr-1">
          {marks && <AiBanner marks={marks} notes={prefill?.notes} />}
          <div className="grid md:grid-cols-2 gap-4">
            <CarrierNumberField id="edit-carrier_number" value={form.carrier_number ?? ""} onChange={set("carrier_number")} excludeId={load?.id} testId="edit-carrier_number" />
            {field("load_number", "Broker ref #")}
            <div className="md:col-span-2">
              <FieldLabel htmlFor="edit-broker_name" mark={marks?.broker_name} testId="edit-broker_name">Broker / Customer</FieldLabel>
              <BrokerPicker id="edit-broker_name" brokerId={form.broker_id} name={form.broker_name ?? ""} aiDetails={prefill?.broker} testId="edit-broker"
                onChange={({ broker_id, broker_name }) => { set("broker_name")(broker_name); setForm((f) => ({ ...f, broker_id })); }} />
            </div>
            {field("rate", "Rate (USD)", { type: "number", step: "0.01", min: "0" })}
            <div>
              <Label>Assigned driver</Label>
              <div className="mt-1.5">
                <DriverSelect load={{ ...load, assigned_driver_id: form.assigned_driver_id }} drivers={drivers} onChange={set("assigned_driver_id")} testId="edit-driver-select" />
              </div>
            </div>
            <div className="md:col-span-2">
              <FieldLabel htmlFor="edit-pickup" mark={marks?.pickup_location} testId="edit-pickup">Pickup</FieldLabel>
              <div className="mt-1.5"><AddressInput id="edit-pickup" value={form.pickup_location ?? ""} onChange={set("pickup_location")} placeholder="City, ST or street address" testId="edit-pickup-input" /></div>
            </div>
            <div className="md:col-span-2">
              <FieldLabel htmlFor="edit-delivery" mark={marks?.delivery_location} testId="edit-delivery">Delivery</FieldLabel>
              <div className="mt-1.5"><AddressInput id="edit-delivery" value={form.delivery_location ?? ""} onChange={set("delivery_location")} placeholder="City, ST or street address" testId="edit-delivery-input" /></div>
              <p className="mt-1.5 text-xs text-slate-500">{ADDRESS_HINT}</p>
            </div>
            {field("pickup_date", "Pickup date", { type: "date" })}
            {field("delivery_date", "Delivery date", { type: "date" })}
            <div>
              <FieldLabel htmlFor="edit-pickup_appt" mark={marks?.pickup_appt} testId="edit-pickup_appt">Pickup appointment</FieldLabel>
              <AppointmentInput value={form.pickup_appt} flagged={marks?.pickup_appt === "flagged"} onChange={set("pickup_appt")} testId="edit-pickup_appt-input" />
            </div>
            <div>
              <FieldLabel htmlFor="edit-delivery_appt" mark={marks?.delivery_appt} testId="edit-delivery_appt">Delivery appointment</FieldLabel>
              <AppointmentInput value={form.delivery_appt} flagged={marks?.delivery_appt === "flagged"} onChange={set("delivery_appt")} testId="edit-delivery_appt-input" />
            </div>
          </div>
          {load?.invoice && (
            <p className="text-xs text-slate-500" data-testid="edit-invoice-note">
              {invoiceSent
                ? "This invoice is already marked Sent, so its amount and broker will not change."
                : "The draft invoice will update to the new rate and broker."}
            </p>
          )}
          {error && <p className="text-sm text-rose-600" data-testid="edit-load-error">{error}</p>}
          {marks && <AiConfirm checked={confirmed} onChange={setConfirmed} />}
          <DialogFooter className="gap-2 sm:gap-2">
            <Button type="button" variant="outline" onClick={onClose} disabled={busy} data-testid="edit-load-cancel-btn">Cancel</Button>
            <Button type="submit" disabled={!valid || busy || (marks && !confirmed)} className="bg-[#002FA7] hover:bg-[#001A75]" data-testid="edit-load-save-btn">
              {busy ? "Saving…" : "Save changes"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};
